import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { HttpStatusCode } from 'constantsValues';
import { ConsentUpdateRequest, CustomerConsent } from 'generated-api/bff/api';
import { consentService } from 'services/ConsentService';

export const fetchConsents = createAsyncThunk<CustomerConsent[]>('consents/fetchConsents', async () => {
	return consentService.fetch();
});

export const updateConsents = createAsyncThunk<CustomerConsent[], Array<ConsentUpdateRequest>>(
	'consents/updateConsents',
	async (consentUpdateRequest) => {
		return consentService.update(consentUpdateRequest);
	}
);

const applyOptimisticUpdate = (
	consents: CustomerConsent[] | null,
	updates: Array<ConsentUpdateRequest>
): CustomerConsent[] | null => {
	if (!consents) return null;
	const updateMap = new Map(updates.map((u) => [u.type, u.value === 'true']));
	return consents.map((consent) => ({
		...consent,
		consent_channels: consent.consent_channels?.map((channel) => {
			const next = updateMap.get(channel.channel_developer_name ?? '');
			return next !== undefined ? { ...channel, channel_is_active: next } : channel;
		}),
	}));
};

interface ConsentState {
	consents: CustomerConsent[] | null;
	previousConsents: CustomerConsent[] | null;
	status: HttpStatusCode | null;
	updateStatus: HttpStatusCode | null;
	error: string | null;
}

const initialState: ConsentState = {
	consents: null,
	previousConsents: null,
	status: HttpStatusCode.Idle,
	updateStatus: HttpStatusCode.Idle,
	error: null,
};

const consentSlice = createSlice({
	name: 'consents',
	initialState,
	reducers: {
		clearConsentCache(state) {
			state.consents = null;
			state.status = HttpStatusCode.Idle;
			state.updateStatus = HttpStatusCode.Idle;
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchConsents.pending, (state) => {
				state.status = HttpStatusCode.Loading;
				state.error = null;
			})
			.addCase(fetchConsents.fulfilled, (state, action) => {
				state.status = action.payload.length === 0 ? HttpStatusCode.NoContent : HttpStatusCode.OK;
				state.consents = action.payload;
			})
			.addCase(fetchConsents.rejected, (state, action) => {
				state.status = HttpStatusCode.InternalServerError;
				state.error = action.error.message || 'Failed to fetch consents';
			})
			.addCase(updateConsents.pending, (state, action) => {
				// Snapshot current state so we can roll back if the API fails.
				state.previousConsents = state.consents;
				state.consents = applyOptimisticUpdate(state.consents, action.meta.arg);
				state.updateStatus = HttpStatusCode.Loading;
				state.error = null;
			})
			.addCase(updateConsents.fulfilled, (state, action) => {
				state.previousConsents = null;
				state.updateStatus = HttpStatusCode.OK;
				state.consents = action.payload;
			})
			.addCase(updateConsents.rejected, (state, action) => {
				state.consents = state.previousConsents ?? state.consents;
				state.previousConsents = null;
				state.updateStatus = HttpStatusCode.InternalServerError;
				state.error = action.error.message || 'Failed to update consents';
			});
	},
});

export const { clearConsentCache } = consentSlice.actions;
export default consentSlice.reducer;
